import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    TemplateRef,
    ViewEncapsulation,
    computed,
    input,
    output
} from '@angular/core';
import { PaginatorModule } from 'primeng/paginator';
import { FcpButtonComponent, ButtonSeverity, ButtonVariant } from '../fcp-button';

@Component({
    selector: 'fcp-paginator',
    imports: [CommonModule, PaginatorModule, FcpButtonComponent],
    styleUrl: './fcp-paginator.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `
        <div class="fcp-paginator-layout" [class.fcp-paginator-layout-load-more]="isLoadMore()">
            <p-paginator
                [totalRecords]="totalRecords()"
                [rows]="rows()"
                [first]="first()"
                [styleClass]="isLoadMore() ? loadMoreStyleClass() : styleClass()"
                [pageLinkSize]="pageLinkSize()"
                [alwaysShow]="alwaysShow()"
                [dropdownAppendTo]="dropdownAppendTo()"
                [templateLeft]="templateLeft()"
                [templateRight]="templateRight()"
                [dropdownScrollHeight]="dropdownScrollHeight()"
                [currentPageReportTemplate]="
                    isLoadMore() ? loadMoreCurrentPageReportTemplate() : currentPageReportTemplate()
                "
                [showCurrentPageReport]="isLoadMore() ? true : showCurrentPageReport()"
                [showFirstLastIcon]="isLoadMore() ? false : showFirstLastIcon()"
                [rowsPerPageOptions]="isLoadMore() ? undefined : rowsPerPageOptions()"
                [showJumpToPageDropdown]="isLoadMore() ? false : showJumpToPageDropdown()"
                [showJumpToPageInput]="isLoadMore() ? false : showJumpToPageInput()"
                [jumpToPageItemTemplate]="jumpToPageItemTemplate()"
                [showPageLinks]="isLoadMore() ? false : showPageLinks()"
                [locale]="locale()"
                [dropdownItemTemplate]="dropdownItemTemplate()"
                (onPageChange)="onPageChange.emit($event)"
            >
                @if (!isLoadMore()) {
                    <ng-template pTemplate="firstpagelinkicon">
                        <span class="fc-font-icon fc-font-icon-chevron-double-left"></span>
                    </ng-template>
                    <ng-template pTemplate="previouspagelinkicon">
                        <span class="fc-font-icon fc-font-icon-chevron-left"></span>
                    </ng-template>
                    <ng-template pTemplate="nextpagelinkicon">
                        <span class="fc-font-icon fc-font-icon-chevron-right"></span>
                    </ng-template>
                    <ng-template pTemplate="lastpagelinkicon">
                        <span class="fc-font-icon fc-font-icon-chevron-double-right"></span>
                    </ng-template>
                }
            </p-paginator>
            @if (isLoadMore()) {
                <fcp-button
                    [label]="loadMoreLabel()"
                    [buttonSeverity]="buttonSeverity.Secondary"
                    [buttonVariant]="buttonVariant.Outlined"
                    [disabled]="!hasMore()"
                    (onClick)="onLoadMoreClick()"
                />
            }
        </div>
    `
})
export class FcpPaginatorComponent {
    e2e = input<string | null>(null);
    totalRecords = input<number>(0);
    rows = input<number>(0);
    first = input<number>(0);
    pageLinkSize = input<number>(5);
    alwaysShow = input<boolean>(true);
    dropdownAppendTo = input<Element | null>(null);
    templateLeft = input<TemplateRef<PaginatorState> | null>(null);
    templateRight = input<TemplateRef<PaginatorState> | null>(null);
    dropdownScrollHeight = input<string>('200px');
    currentPageReportTemplate = input<string>('{currentPage} of {totalPages}');
    showCurrentPageReport = input<boolean>(false);
    showFirstLastIcon = input<boolean>(true);
    rowsPerPageOptions = input<number[] | undefined>(undefined);
    showJumpToPageDropdown = input<boolean>(false);
    showJumpToPageInput = input<boolean>(false);
    jumpToPageItemTemplate = input<TemplateRef<{ $implicit: number }> | null>(null);
    showPageLinks = input<boolean>(true);
    locale = input<string | undefined>(undefined);
    dropdownItemTemplate = input<TemplateRef<{ $implicit: number }> | null>(null);
    styleClass = input<string>('');
    variant = input<PaginatorVariant>(PaginatorVariant.Default);
    loadMoreLabel = input<string>('Load more');
    loadMoreCurrentPageReportTemplate = input<string>('Showing {first} to {last} of {totalRecords} entries');

    onPageChange = output<PaginatorState>();

    protected readonly paginatorVariant = PaginatorVariant;
    protected readonly buttonSeverity = ButtonSeverity;
    protected readonly buttonVariant = ButtonVariant;

    protected readonly isLoadMore = computed(() => this.variant() === this.paginatorVariant.LoadMore);
    protected readonly hasMore = computed(() => {
        const rows = this.rows();
        if (rows <= 0) {
            return false;
        }

        return this.first() + rows < this.totalRecords();
    });
    protected readonly loadMoreStyleClass = computed(() => `fcp-paginator-load-more ${this.styleClass()}`.trim());

    protected onLoadMoreClick(): void {
        if (!this.hasMore()) {
            return;
        }

        const pageCount = this.rows() > 0 ? Math.ceil(this.totalRecords() / this.rows()) : 0;
        const lastPageStart = Math.max(pageCount - 1, 0) * this.rows();
        const nextFirst = Math.min(this.first() + this.rows(), lastPageStart);
        const page = this.rows() > 0 ? Math.floor(nextFirst / this.rows()) : 0;

        this.onPageChange.emit({
            first: nextFirst,
            rows: this.rows(),
            page,
            pageCount
        });
    }
}

export enum PaginatorVariant {
    Default = 'default',
    LoadMore = 'load-more'
}

export interface PaginatorState {
    page: number;
    first: number;
    rows: number;
    pageCount: number;
}
